import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import {
  Modal,
  Box,
  Button,
  Alert,
  Chip,
  Card,
  Stack,
  IconButton
} from '@mui/material'
import {
  Close as CloseIcon,
  Calculate as CalculateIcon,
  Info as InfoIcon
} from '@mui/icons-material'
import { 
  CustomDefinition, 
  CustomMetricSource, 
  CustomMetricValueType
} from '../../../interfaces/db'
import { MetricResult } from '../../../interfaces/metrics'
import { SessionDataResult } from '../../../interfaces/sessions'
import { getServicesManager } from '../../../services'
import { formatMetricType } from '../utils'
import { getCustomCode } from '../cfg'
import { CustomCode } from '../cfg'
import { History } from '../../History/History'
import { SessionDetailView } from '../../History/views'
import { Typography } from '../../../interfaces/typography'

interface TestMetricModalProps {
  open: boolean
  metric: CustomDefinition | null
  onClose: () => void
  onEdit?: (metric: CustomDefinition) => void
}

export function TestMetricModal({ open, metric, onClose, onEdit }: TestMetricModalProps) {
  const team_id = useSelector((state: RootState) => state.user.value?.team_id ?? '')
  const [selectedSessions, setSelectedSessions] = useState<string[]>([])
  const [sessionData, setSessionData] = useState<SessionDataResult | null>(null)
  const [testing, setTesting] = useState(false)
  const [analysisResults, setAnalysisResults] = useState<MetricResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Get the code options for the current team, if any
  const codeOptions: CustomCode[] = getCustomCode(team_id) ?? []

  // Watch for changes to the selected session
  useEffect(() => {
    if (selectedSessions.length > 0) {
      setAnalysisResults(null)  // Clear previous results
      loadSessionData(selectedSessions[0])
    }
  }, [selectedSessions])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedSessions([])
      setSessionData(null)
      setAnalysisResults(null)
      setError(null)
    }
  }, [open])

  const loadSessionData = async (sessionId: string) => {
    try {
      const services = getServicesManager()
      const data = await services.getSessionData(sessionId)
      if (data && typeof data !== 'number') {
        setSessionData(data)
      }
    } catch (err) {
      console.error('Failed to load session data:', err)
    }
  }

  const handleTestMetric = async () => {
    if (selectedSessions.length === 0 || !sessionData || !metric) return
    setTesting(true)
    setError(null)
    try {
      const services = getServicesManager()
      const result = await services.requestCustomMetricTesting(
        sessionData.session.session_id,
        [metric]
      )
      
      if (!result) {
        throw new Error('No results returned from server')
      }
      
      setAnalysisResults(result)
      setTesting(false)
    } catch (error) {
      console.error('Error testing metric:', error)
      
      let errorMessage = 'Failed to test metric. Please try again.'
      
      if (error instanceof Error) {
        // Check for specific error types
        if (error.message.includes('Network')) {
          errorMessage = 'Network error: Unable to connect to server. Please check your connection.'
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timeout: The metric test is taking too long. Try with a shorter session.'
        } else if (error.message.includes('invalid')) {
          errorMessage = 'Invalid metric configuration: Please check your metric settings and try again.'
        } else if (error.message.includes('No results')) {
          errorMessage = 'No results returned: The server processed the request but returned no data.'
        } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
          errorMessage = 'Authentication error: Please refresh the page and try again.'
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error: There was an internal server error. Please try again later.'
        } else if (error.message) {
          // Use the specific error message if available
          errorMessage = `Error: ${error.message}`
        }
      }
      
      setError(errorMessage)
      setTesting(false)
    }
  }

  const renderMetricResult = () => {
    if (!analysisResults?.metric || !metric || metric.metric_idx === null) {
      return (
        <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 text-center">
          <Typography variant="mediumParagraph" color="#6B7280">
            No results yet - select a session and click test
          </Typography>
        </div>
      )
    }

    const metricData = analysisResults.metric
    const metricKey = `metric_${metric.metric_idx}` as keyof typeof metricData
    const textKey = `text_${metric.metric_idx}` as keyof typeof metricData
    const metricValue = metricData[metricKey]
    const textValue = metricData[textKey]

    const detail = analysisResults.detail?.metric_to_explanation_json_str
    let explanation = null
    if (detail) {
      try {
        const explanations = JSON.parse(detail)
        explanation = explanations[metric.metric_idx.toString()]
      } catch (e) {
        console.error('Failed to parse explanation:', e)
      }
    }

    const renderEnumValue = () => {
      if (metric.value_source === CustomMetricSource.CODE && metric.value_code_str) {
        const codeOption = codeOptions.find(c => c.name === metric.value_code_str)
        if (codeOption?.enum_options && metricValue !== null) {
          return String(codeOption.enum_options[Number(metricValue)] || 'Invalid category')
        }
      }
      if (metricValue === null || !metric.value_type_enum_str) return 'No result'
      const categories = metric.value_type_enum_str.split(',').map(c => c.trim())
      const index = Number(metricValue)
      return String(categories[index] || 'Invalid category')
    }

    return (
      <div className="flex gap-4">
        {/* Result Section */}
        <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <Typography variant="h4" color="#1F2937" fontWeight={600}>
            Result
          </Typography>
          
          {metric.value_type === CustomMetricValueType.TEXT ? (
            <div className="p-3 bg-gray-50 border rounded-lg">
              <Typography variant="mediumParagraph" color="#374151">
                {String(textValue || 'No result')}
              </Typography>
            </div>
          ) : metric.value_type === CustomMetricValueType.BOOLEAN ? (
            <div className="flex items-center gap-3 p-3 bg-gray-50 border rounded-lg">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                metricValue ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <Typography variant="smallParagraph" color={metricValue ? '#059669' : '#DC2626'} fontWeight={700}>
                  {metricValue ? '✓' : '✕'}
                </Typography>
              </div>
              <Typography variant="h4" color={metricValue ? '#059669' : '#DC2626'} fontWeight={700}>
                {metricValue ? 'Yes' : 'No'}
              </Typography>
            </div>
          ) : metric.value_type === CustomMetricValueType.PERCENT ? (
            <div className="p-3 bg-gray-50 border rounded-lg space-y-2">
              <div className="flex items-center gap-3">
                <Typography variant="h2" color="#1F2937" fontWeight={700} className="text-2xl">
                  {Math.round(Number(metricValue || 0) * 100)}%
                </Typography>
                <div className="flex-1 max-w-32">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        Number(metricValue) * 100 >= 80 ? 'bg-green-500' 
                        : Number(metricValue) * 100 >= 50 ? 'bg-yellow-500' 
                        : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.round(Number(metricValue || 0) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : metric.value_type === CustomMetricValueType.ENUM ? (
            <div className="p-3 bg-gray-50 border rounded-lg">
              <Chip 
                label={renderEnumValue()}
                sx={{ 
                  backgroundColor: '#F3E8FF',
                  color: '#7C3AED',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: '28px',
                  px: 1
                }}
              />
            </div>
          ) : null}
        </div>

        {/* Explanation Section */}
        {explanation && (
          <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4">
            <Typography variant="h4" color="#1F2937" fontWeight={600} className="mb-3">
              AI Explanation
            </Typography>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <InfoIcon sx={{ fontSize: 18, color: '#2563EB', flexShrink: 0, mt: 0.25 }} />
                <Typography variant="mediumParagraph" color="#1E40AF" className="leading-relaxed">
                  {String(explanation)}
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (!metric) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Box sx={{
        width: '95vw',
        maxWidth: 1400,
        height: '90vh',
        backgroundColor: 'background.paper',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}>
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <Typography variant="h2" color="#1F2937" fontWeight={700}>
              Test Metric: {metric.label}
            </Typography>
            <Chip 
              label={formatMetricType(metric.value_type)}
              sx={{ 
                backgroundColor: '#2563EB',
                color: 'white',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            {onEdit && (
              <button
                onClick={() => onEdit(metric)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-50 border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Edit Metric
              </button>
            )}
            <IconButton 
              onClick={onClose} 
              size="large"
              sx={{ 
                backgroundColor: 'white',
                boxShadow: 1,
                '&:hover': { backgroundColor: '#F9FAFB' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {/* Top Section - Test Controls and Results */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="space-y-3">
            {/* Test Controls */}
            <div className="flex items-center justify-between">
              <div>
                <Typography variant="h4" color="#1F2937" fontWeight={600} className="mb-1">
                  Quick Test
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280">
                  Select a session below and test your metric
                </Typography>
              </div>
              
              <button
                onClick={handleTestMetric}
                disabled={selectedSessions.length === 0 || testing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {testing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <CalculateIcon sx={{ fontSize: 16 }} />
                    {selectedSessions.length === 0 ? 'Select Session to Test' : 'Test Metric'}
                  </>
                )}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  py: 0.5,
                  px: 2,
                  borderRadius: 2
                }}
              >
                {error}
              </Alert>
            )}

            {/* Results Display */}
            {renderMetricResult()}
          </div>
        </div>

        {/* Bottom Section - Session Selection (Full Width) */}
        <div className="flex-1 overflow-hidden p-4">
          <Typography variant="h4" color="#1F2937" fontWeight={600} className="mb-3">
            Select a Session to Analyze
          </Typography>
          
          <div className="h-full overflow-hidden border border-gray-200 rounded-lg bg-white shadow-sm">
            <History 
              view={SessionDetailView.ANALYSIS}
              onSessionSelect={setSelectedSessions}
              selectedSessions={selectedSessions}
            />
          </div>
        </div>
      </Box>
    </Modal>
  )
} 